import { createFrameClock } from './frame-clock';
import { createTrack } from './track';
export class Timeline {
    fps;
    clock;
    nodes = new Map();
    tracks = [];
    currentFrame = 0;
    constructor(options = {}) {
        this.fps = options.fps ?? 24;
        this.clock = createFrameClock(this.fps, options.durationFrames ?? 0);
    }
    get durationFrames() {
        return this.clock.durationFrames;
    }
    get durationSeconds() {
        return this.clock.frameToTime(this.durationFrames);
    }
    get frame() {
        return this.currentFrame;
    }
    get nodeCount() {
        return this.nodes.size;
    }
    get trackList() {
        return this.tracks;
    }
    addTrack(options) {
        const track = createTrack({ ...options, index: options.index ?? this.tracks.length });
        this.tracks.push(track);
        return track;
    }
    removeTrack(id) {
        const index = this.tracks.findIndex((t) => t.id === id);
        if (index === -1)
            return false;
        this.tracks.splice(index, 1);
        return true;
    }
    addNode(node) {
        if (this.nodes.has(node.id)) {
            throw new Error(`Node with id ${node.id} already exists`);
        }
        this.nodes.set(node.id, node);
        this.recomputeDuration();
        return this;
    }
    removeNode(id) {
        const node = this.nodes.get(id);
        if (!node)
            return false;
        node.dispose();
        this.nodes.delete(id);
        this.recomputeDuration();
        return true;
    }
    getNode(id) {
        return this.nodes.get(id);
    }
    nodesAt(frame) {
        const result = [];
        for (const node of this.nodes.values()) {
            if (node.isActiveAt(frame))
                result.push(node);
        }
        return result;
    }
    seek(frame) {
        this.currentFrame = this.clock.clampFrame(frame);
    }
    seekTime(seconds) {
        this.currentFrame = this.clock.clampFrame(this.clock.timeToFrame(seconds));
    }
    async prepare() {
        await Promise.all([...this.nodes.values()].map((n) => n.prepare({ fps: this.fps, width: 0, height: 0 })));
    }
    render(frame) {
        const targetFrame = frame === undefined ? this.currentFrame : this.clock.clampFrame(frame);
        const time = this.clock.frameToTime(targetFrame);
        for (const node of this.nodes.values()) {
            node.render({
                frame: targetFrame,
                time,
                fps: this.fps,
                deltaTime: this.fps > 0 ? 1 / this.fps : 0,
            });
        }
        this.currentFrame = targetFrame;
    }
    eventsBetween(fromFrame, toFrame) {
        const events = [];
        for (const node of this.nodes.values()) {
            if (node.start >= fromFrame && node.start <= toFrame) {
                events.push({ frame: node.start, nodeId: node.id, type: 'start' });
            }
            const end = node.start + node.duration;
            if (end >= fromFrame && end <= toFrame) {
                events.push({ frame: end, nodeId: node.id, type: 'end' });
            }
        }
        return events.sort((a, b) => a.frame - b.frame);
    }
    dispose() {
        for (const node of this.nodes.values()) {
            node.dispose();
        }
        this.nodes.clear();
        this.tracks = [];
        this.currentFrame = 0;
    }
    recomputeDuration() {
        let maxEnd = 0;
        for (const node of this.nodes.values()) {
            maxEnd = Math.max(maxEnd, node.start + node.duration);
        }
        const newClock = createFrameClock(this.fps, maxEnd);
        // Patch clock duration in place to preserve the frame-clock reference
        Object.assign(this.clock, newClock);
    }
}
export function createTimeline(options) {
    return new Timeline(options);
}
//# sourceMappingURL=timeline.js.map